import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-hot-toast";
import { useTranslation } from "../../../hooks/useTranslation";
import LoaderComponent from "../../Loader/Loader";
import SectionHeadingStyle3 from "../../SectionHeading/SectionHeadingStyle3";
import Spacing from "../../Spacing";
import styled from "styled-components";

const ClientsList = ({ darkMode }) => {
  const { translate } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingActions, setLoadingActions] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [viewMode, setViewMode] = useState("table"); // table or grid

  // Helper functions
  const getUserStatus = (user) => {
    if (user.accessLevel < 0) return "suspended";
    if (user.verified && user.accessLevel >= 0) return "active";
    if (!user.verified) return "inactive";
    return "pending";
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "#10b981";
      case "inactive":
        return "#ef4444";
      case "pending":
        return "#f59e0b";
      case "suspended":
        return "#6b7280";
      default:
        return "#6b7280";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "active":
        return "Ativo";
      case "inactive":
        return "Inativo";
      case "pending":
        return "Pendente";
      case "suspended":
        return "Suspenso";
      default:
        return status;
    }
  };

  const getAccessLevelText = (level) => {
    if (level >= 2) return "Administrador";
    if (level === 1) return "Moderador";
    if (level === 0) return "Usuário";
    return "Suspenso";
  };

  const getAccessLevelColor = (level) => {
    if (level >= 2) return "#8b5cf6";
    if (level === 1) return "#06b6d4";
    if (level === 0) return "#10b981";
    return "#ef4444";
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    if (timestamp._seconds) {
      return new Date(timestamp._seconds * 1000).toLocaleDateString("pt-BR");
    }
    return new Date(timestamp).toLocaleDateString("pt-BR");
  };

  // Memoized filtered and sorted data
  const filteredUsers = useMemo(() => {
    let filtered = users;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(user => 
        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.cpf?.includes(searchTerm)
      );
    }

    // Apply status filter
    if (filterStatus !== "all") {
      filtered = filtered.filter(user => {
        const status = getUserStatus(user);
        return status === filterStatus;
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case "name":
          aValue = a.name || "";
          bValue = b.name || "";
          break;
        case "email":
          aValue = a.email || "";
          bValue = b.email || "";
          break;
        case "accessLevel":
          aValue = a.accessLevel || 0;
          bValue = b.accessLevel || 0;
          break;
        case "createdAt":
          aValue = a.createdAt || new Date(0);
          bValue = b.createdAt || new Date(0);
          break;
        default:
          aValue = a.createdAt || new Date(0);
          bValue = b.createdAt || new Date(0);
      }
      
      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [users, searchTerm, filterStatus, sortBy, sortOrder]);

  // Statistics
  const stats = useMemo(() => {
    const total = users.length;
    const active = users.filter(user => getUserStatus(user) === "active").length;
    const inactive = users.filter(user => getUserStatus(user) === "inactive").length;
    const pending = users.filter(user => getUserStatus(user) === "pending").length;
    const suspended = users.filter(user => getUserStatus(user) === "suspended").length;

    return { total, active, inactive, pending, suspended };
  }, [users]);

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("userToken");

        if (!token) {
          toast.error(translate("clientsList.errorLoading"));
          return;
        }

        const response = await fetch(
          `https://listusers-qj6yfdqbiq-uc.a.run.app`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        
        if (!response.ok) {
          throw new Error("Erro ao buscar usuários");
        }
        
        const data = await response.json();
        console.log("Resposta da API:", data);
        setUsers(data || []);
      } catch (error) {
        console.error("Erro ao buscar usuários:", error);
        toast.error(translate("clientsList.errorLoading"));
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const handleStatusChange = async (userId, newStatus) => {
    setLoadingActions((prev) => ({ ...prev, [`status-${userId}`]: true }));
    try {
      const token = localStorage.getItem("userToken");

      if (!token) {
        toast.error(translate("clientsList.errorToken"));
        return;
      }

      const response = await fetch(
        `https://updateclientstatus-qj6yfdqbiq-uc.a.run.app`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            clientId: userId,
            status: newStatus,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Erro ao atualizar status");
      }

      const result = await response.json();
      
      if (result.success) {
        toast.success("Status atualizado com sucesso!");
        setUsers(prev => 
          prev.map(user => 
            user.uid === userId 
              ? { ...user, accessLevel: newStatus === "suspended" ? -1 : 0, verified: newStatus === "active" }
              : user
          )
        );
      } else {
        toast.error("Erro ao atualizar status");
      }
    } catch (error) {
      console.error("Erro ao atualizar status:", error);
      toast.error("Erro ao atualizar status");
    } finally {
      setLoadingActions((prev) => ({ ...prev, [`status-${userId}`]: false }));
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Tem certeza que deseja excluir este usuário?")) {
      return;
    }

    setLoadingActions((prev) => ({ ...prev, [`delete-${userId}`]: true }));
    try {
      const token = localStorage.getItem("userToken");

      if (!token) {
        toast.error(translate("clientsList.errorToken"));
        return;
      }

      const response = await fetch(
        `https://deleteclient-qj6yfdqbiq-uc.a.run.app`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            clientId: userId,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Erro ao deletar usuário");
      }

      const result = await response.json();
      
      if (result.success) {
        toast.success("Usuário excluído com sucesso!");
        setUsers(prev => prev.filter(user => user.uid !== userId));
      } else {
        toast.error("Erro ao excluir usuário");
      }
    } catch (error) {
      console.error("Erro ao excluir usuário:", error);
      toast.error("Erro ao excluir usuário");
    } finally {
      setLoadingActions((prev) => ({ ...prev, [`delete-${userId}`]: false }));
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setFilterStatus("all");
    setSortBy("createdAt");
    setSortOrder("desc");
  };

  if (loading) {
    return (
      <LoadingContainer>
        <LoaderComponent size={60} borderThickness={8} />
        <LoadingText>Carregando usuários...</LoadingText>
      </LoadingContainer>
    );
  }

  return (
    <>
      <Spacing lg="145" md="80" />
      <Container>
        <HeaderSection>
          <BackButton onClick={() => navigate("/dashboard-admin")}>
            <ArrowIcon>←</ArrowIcon>
            Voltar ao Dashboard
          </BackButton>
          
          <SectionHeadingStyle3
            title="Gerenciamento de Usuários"
            subTitle="Visualize e gerencie todos os usuários da plataforma"
            variant="cs_style_1 text-center"
          />
        </HeaderSection>

        <Spacing lg="60" md="40" />

        {/* Statistics Cards */}
        <StatsContainer>
          <StatCard>
            <StatIcon>👥</StatIcon>
            <StatContent>
              <StatNumber>{stats.total}</StatNumber>
              <StatLabel>Total de Usuários</StatLabel>
            </StatContent>
          </StatCard>
          
          <StatCard>
            <StatIcon style={{ color: "#10b981" }}>✅</StatIcon>
            <StatContent>
              <StatNumber>{stats.active}</StatNumber>
              <StatLabel>Usuários Ativos</StatLabel>
            </StatContent>
          </StatCard>
          
          <StatCard>
            <StatIcon style={{ color: "#ef4444" }}>❌</StatIcon>
            <StatContent>
              <StatNumber>{stats.inactive}</StatNumber>
              <StatLabel>Usuários Inativos</StatLabel>
            </StatContent>
          </StatCard>
          
          <StatCard>
            <StatIcon style={{ color: "#f59e0b" }}>⏳</StatIcon>
            <StatContent>
              <StatNumber>{stats.pending}</StatNumber>
              <StatLabel>Pendentes</StatLabel>
            </StatContent>
          </StatCard>
          
          <StatCard>
            <StatIcon style={{ color: "#6b7280" }}>🚫</StatIcon>
            <StatContent>
              <StatNumber>{stats.suspended}</StatNumber>
              <StatLabel>Suspensos</StatLabel>
            </StatContent>
          </StatCard>
        </StatsContainer>

        <Spacing lg="40" md="30" />

        {/* Controls Section */}
        <ControlsSection>
          <ControlsLeft>
            <SearchContainer>
              <SearchIcon>🔍</SearchIcon>
              <SearchInput
                type="text"
                placeholder="Buscar por nome, email ou CPF..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchContainer>
            
            <FilterContainer>
              <FilterSelect
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">Todos os Status</option>
                <option value="active">Ativos</option>
                <option value="inactive">Inativos</option>
                <option value="pending">Pendentes</option>
                <option value="suspended">Suspensos</option>
              </FilterSelect>

              <FilterSelect
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="createdAt">Data de Criação</option>
                <option value="name">Nome</option>
                <option value="email">Email</option>
                <option value="accessLevel">Nível de Acesso</option>
              </FilterSelect>

              <SortButton
                onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
                title={sortOrder === "asc" ? "Ordenar Decrescente" : "Ordenar Crescente"}
              >
                {sortOrder === "asc" ? "↑" : "↓"}
              </SortButton>
            </FilterContainer>
          </ControlsLeft>

          <ControlsRight>
            <ViewToggle>
              <ViewButton
                active={viewMode === "table"}
                onClick={() => setViewMode("table")}
              >
                📊 Tabela
              </ViewButton>
              <ViewButton
                active={viewMode === "grid"}
                onClick={() => setViewMode("grid")}
              >
                🎴 Cards
              </ViewButton>
            </ViewToggle>
            
            <ClearFiltersButton onClick={clearFilters}>
              🗑️ Limpar Filtros
            </ClearFiltersButton>
          </ControlsRight>
        </ControlsSection>

        <Spacing lg="30" md="20" />

        {/* Results Info */}
        <ResultsInfo>
          <ResultsCount>
            {filteredUsers.length} usuário{filteredUsers.length !== 1 ? 's' : ''} encontrado{filteredUsers.length !== 1 ? 's' : ''}
          </ResultsCount>
          {searchTerm && (
            <SearchTerm>
              para "{searchTerm}"
            </SearchTerm>
          )}
        </ResultsInfo>

        <Spacing lg="20" md="15" />

        {/* Content */}
        {filteredUsers.length === 0 ? (
          <EmptyState>
            <EmptyIcon>👥</EmptyIcon>
            <EmptyTitle>Nenhum usuário encontrado</EmptyTitle>
            <EmptyDescription>
              {searchTerm || filterStatus !== "all" 
                ? "Tente ajustar os filtros de busca"
                : "Não há usuários cadastrados na plataforma"
              }
            </EmptyDescription>
          </EmptyState>
        ) : viewMode === "table" ? (
          <TableContainer>
            <Table>
              <thead>
                <tr>
                  <th>Usuário</th>
                  <th>Contato</th>
                  <th>Nível de Acesso</th>
                  <th>Status</th>
                  <th>Data de Criação</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.uid}>
                    <td>
                      <UserInfo>
                        <UserAvatar src="/perfil.png" alt={user.name} />
                        <UserDetails>
                          <UserName>{user.name || "Nome não informado"}</UserName>
                          <UserId>ID: {user.uid}</UserId>
                        </UserDetails>
                      </UserInfo>
                    </td>
                    <td>
                      <ContactInfo>
                        <ContactItem>
                          <ContactIcon>📧</ContactIcon>
                          {user.email || "N/A"}
                        </ContactItem>
                        <ContactItem>
                          <ContactIcon>🆔</ContactIcon>
                          {user.cpf || "N/A"}
                        </ContactItem>
                      </ContactInfo>
                    </td>
                    <td>
                      <AccessLevelBadge color={getAccessLevelColor(user.accessLevel)}>
                        {getAccessLevelText(user.accessLevel)}
                      </AccessLevelBadge>
                    </td>
                    <td>
                      <StatusBadge color={getStatusColor(getUserStatus(user))}>
                        {getStatusText(getUserStatus(user))}
                      </StatusBadge>
                    </td>
                    <td>
                      <DateInfo>
                        {formatDate(user.createdAt)}
                      </DateInfo>
                    </td>
                    <td>
                      <ActionsContainer>
                        <ActionButton
                          onClick={() => navigate(`/client-details/${user.uid}`)}
                          title="Ver detalhes"
                          primary
                        >
                          Ver detalhes
                        </ActionButton>
                      </ActionsContainer>
                    </td>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          </TableContainer>
        ) : (
          <GridContainer>
            {filteredUsers.map((user) => (
              <UserCard key={user.uid}>
                <CardHeader>
                  <UserAvatar src="/perfil.png" alt={user.name} />
                  <CardHeaderInfo>
                    <UserName>{user.name || "Nome não informado"}</UserName>
                    <UserId>ID: {user.uid}</UserId>
                  </CardHeaderInfo>
                  <StatusBadge color={getStatusColor(getUserStatus(user))}>
                    {getStatusText(getUserStatus(user))}
                  </StatusBadge>
                </CardHeader>
                
                <CardBody>
                  <CardInfo>
                    <InfoLabel>📧 Email:</InfoLabel>
                    <InfoValue>{user.email || "N/A"}</InfoValue>
                  </CardInfo>
                  
                  <CardInfo>
                    <InfoLabel>🆔 CPF:</InfoLabel>
                    <InfoValue>{user.cpf || "N/A"}</InfoValue>
                  </CardInfo>
                  
                  <CardInfo>
                    <InfoLabel>🔑 Nível:</InfoLabel>
                    <AccessLevelBadge color={getAccessLevelColor(user.accessLevel)}>
                      {getAccessLevelText(user.accessLevel)}
                    </AccessLevelBadge>
                  </CardInfo>
                  
                  <CardInfo>
                    <InfoLabel>📅 Criado em:</InfoLabel>
                    <InfoValue>{formatDate(user.createdAt)}</InfoValue>
                  </CardInfo>
                </CardBody>
                
                <CardActions>
                  <ActionButton
                    onClick={() => navigate(`/client-details/${user.uid}`)}
                    title="Ver detalhes"
                    primary
                  >
                    Ver detalhes
                  </ActionButton>
                </CardActions>
              </UserCard>
            ))}
          </GridContainer>
        )}
      </Container>
      <Spacing lg="145" md="80" />
    </>
  );
};

// Styled Components
const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 20px;
`;

const LoadingText = styled.p`
  font-size: 18px;
  color: #888;
  margin: 0;
`;

const HeaderSection = styled.div`
  text-align: center;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #c09c5c;
  font-size: 16px;
  cursor: pointer;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: color 0.2s ease;
  padding: 8px 16px;
  border-radius: 8px;

  &:hover {
    color: #a07c4b;
    background-color: rgba(192, 156, 92, 0.1);
  }
`;

const ArrowIcon = styled.span`
  font-size: 18px;
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 15px;
  }
`;

const StatCard = styled.div`
  background: linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%);
  border: 1px solid #333;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 15px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
  }

  @media (max-width: 768px) {
    padding: 15px;
    gap: 10px;
  }
`;

const StatIcon = styled.div`
  font-size: 24px;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(192, 156, 92, 0.1);
  border-radius: 12px;

  @media (max-width: 768px) {
    font-size: 20px;
    width: 40px;
    height: 40px;
  }
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatNumber = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: #fff;
  margin-bottom: 4px;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: #888;
  font-weight: 500;
`;

const ControlsSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  margin-bottom: 20px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ControlsLeft = styled.div`
  display: flex;
  gap: 15px;
  align-items: center;
  flex-wrap: wrap;
  flex: 1;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ControlsRight = styled.div`
  display: flex;
  gap: 15px;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  min-width: 300px;

  @media (max-width: 768px) {
    min-width: auto;
  }
`;

const SearchIcon = styled.span`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #888;
  font-size: 16px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 40px;
  border: 1px solid #c09c5c;
  border-radius: 8px;
  background-color: #121212;
  color: #fff;
  font-size: 14px;
  transition: all 0.2s ease;

  &::placeholder {
    color: #888;
  }

  &:focus {
    outline: none;
    border-color: #a07c4b;
    box-shadow: 0 0 0 3px rgba(192, 156, 92, 0.2);
  }
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const FilterSelect = styled.select`
  padding: 12px 16px;
  border: 1px solid #c09c5c;
  border-radius: 8px;
  background-color: #121212;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #a07c4b;
  }
`;

const SortButton = styled.button`
  padding: 12px 16px;
  border: 1px solid #c09c5c;
  border-radius: 8px;
  background-color: #121212;
  color: #fff;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #a07c4b;
  }
`;

const ViewToggle = styled.div`
  display: flex;
  border: 1px solid #c09c5c;
  border-radius: 8px;
  overflow: hidden;
`;

const ViewButton = styled.button`
  padding: 12px 16px;
  border: none;
  background-color: ${props => props.active ? "#c09c5c" : "#121212"};
  color: ${props => props.active ? "#000" : "#fff"};
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${props => props.active ? "#a07c4b" : "#2a2a2a"};
  }
`;

const ClearFiltersButton = styled.button`
  padding: 12px 16px;
  border: 1px solid #ef4444;
  border-radius: 8px;
  background-color: #121212;
  color: #ef4444;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #ef4444;
    color: #fff;
  }
`;

const ResultsInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const ResultsCount = styled.span`
  font-size: 16px;
  color: #fff;
  font-weight: 600;
`;

const SearchTerm = styled.span`
  font-size: 14px;
  color: #888;
  background-color: #2a2a2a;
  padding: 4px 8px;
  border-radius: 4px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  background-color: #1a1a1a;
  border-radius: 12px;
  border: 1px solid #333;
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 20px;
`;

const EmptyTitle = styled.h3`
  font-size: 24px;
  color: #fff;
  margin: 0 0 10px 0;
`;

const EmptyDescription = styled.p`
  font-size: 16px;
  color: #888;
  margin: 0;
`;

const TableContainer = styled.div`
  background-color: #1a1a1a;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border: 1px solid #333;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  color: #fff;

  th, td {
    padding: 16px;
    text-align: left;
    border-bottom: 1px solid #333;
  }

  th {
    background-color: #2a2a2a;
    font-weight: 600;
    color: #c09c5c;
    font-size: 14px;
  }

  tr:hover {
    background-color: #252525;
  }

  @media (max-width: 768px) {
    font-size: 12px;
    
    th, td {
      padding: 8px;
    }
  }
`;

const TableRow = styled.tr`
  transition: background-color 0.2s ease;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const UserAvatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const UserName = styled.div`
  font-weight: 600;
  color: #fff;
  font-size: 14px;
`;

const UserId = styled.div`
  font-size: 12px;
  color: #888;
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
`;

const ContactIcon = styled.span`
  font-size: 12px;
`;

const AccessLevelBadge = styled.span`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  background-color: ${props => props.color}20;
  color: ${props => props.color};
  border: 1px solid ${props => props.color};
`;

const StatusBadge = styled.span`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  background-color: ${props => props.color}20;
  color: ${props => props.color};
  border: 1px solid ${props => props.color};
`;

const DateInfo = styled.div`
  font-size: 13px;
  color: #888;
`;

const ActionsContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const StatusSelect = styled.select`
  padding: 6px 8px;
  border: 1px solid #c09c5c;
  border-radius: 4px;
  background-color: #121212;
  color: #fff;
  font-size: 12px;
  cursor: pointer;
  min-width: 80px;

  &:focus {
    outline: none;
    border-color: #a07c4b;
  }
`;

const ActionButton = styled.button`
  padding: 6px 8px;
  border: 1px solid ${props => props.danger ? "#ef4444" : props.primary ? "#c09c5c" : "#6b7280"};
  border-radius: 4px;
  background-color: ${props => props.danger ? "#ef444420" : props.primary ? "#c09c5c20" : "#121212"};
  color: ${props => props.danger ? "#ef4444" : props.primary ? "#c09c5c" : "#fff"};
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background-color: ${props => props.danger ? "#ef4444" : props.primary ? "#c09c5c" : "#2a2a2a"};
    color: #fff;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const UserCard = styled.div`
  background: linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%);
  border: 1px solid #333;
  border-radius: 12px;
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
  }
`;

const CardHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #333;
  display: flex;
  align-items: center;
  gap: 15px;
`;

const CardHeaderInfo = styled.div`
  flex: 1;
`;

const CardBody = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CardInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
`;

const InfoLabel = styled.span`
  font-size: 14px;
  color: #888;
  font-weight: 500;
`;

const InfoValue = styled.span`
  font-size: 14px;
  color: #fff;
  font-weight: 500;
`;

const CardActions = styled.div`
  padding: 20px;
  border-top: 1px solid #333;
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
`;

export default ClientsList;
